<template>
  <div>
    <div id="datePickerDiv" class="w-100 d-flex justify-content-center align-items-start p-3 text-dark"
      @click="toggleShowHideDatePicker()">
      <div class="w-100 d-flex justify-content-start align-items-center">
        <div class="me-2 align-self-center">
          <svg class="icon tf-icon-22 text-blue-900">
            <use xlink:href="#___icon___calander"></use>
          </svg>
        </div>

        <div class="datepicker-label">
          <label class="input-title datepicker-input-label">
            {{
              range.start && range.end
                ? `${departureText} - ${returnText}`
                : content.planner_datePicker_depart_return
            }}
          </label>
        </div>
      </div>
    </div>

    <div class="datePickerContainerRef datepicker-input position-absolute" ref="datePickerContainerRef"
      :class="[isOpenedRef ? 'd-block' : 'd-none']">
      <!-- Desktop DatePicker (existing behavior) -->
      <VDatePicker v-if="!isMobile" v-model="selectedDate" :locale="locale" ref="datePickerRef" :columns="2" :rows="1"
        :color="selectedColor" :attributes="attributesRef" :available-dates="availableDates" mode="date"
        :disabled-dates="disabledDates" @dayclick="selectDate" @update:pages="handlePagesChange" :step="1">
        <template #footer>
          <div class="d-flex justify-content-around align-items-center m-2 border-top">
            <label class="input-title fw-bold m-2">
              {{
                range.start
                  ? `${departureText} - ${returnText}`
                  : content.planner_datePicker_depart_return
              }}
            </label>
          </div>
        </template>
      </VDatePicker>
    </div>

    <!-- Mobile Custom Popup - Outside container to cover entire screen -->
    <div v-if="isMobile && isOpenedRef" class="mobile-datepicker-popup" @click="handleMobilePopupClick">
      <div
        class="mobile-datepicker-content bg-white rounded-4 w-100 h-100 overflow-auto position-relative d-flex flex-column align-items-center"
        @click.stop>
        <!-- Sticky Header -->
        <div class="datepicker-header-sticky">
          <div class="d-flex justify-content-end w-100 p-3">
            <ClsBtn @click="toggleShowHideDatePicker(false)" />
          </div>
        </div>

        <!-- Scrollable Calendar Content -->
        <div class="datepicker-calendar-content">
          <VDatePicker v-model="selectedDate" :locale="locale" ref="datePickerRef" :columns="1" :rows="6"
            :color="selectedColor" :attributes="attributesRef" :available-dates="availableDates" mode="date"
            :disabled-dates="disabledDates" @dayclick="selectDate" @update:pages="handlePagesChange">
          </VDatePicker>
        </div>

        <!-- Sticky Footer -->
        <div class="datepicker-footer-sticky">
          <div class="d-flex justify-content-around align-items-center p-3 border-top bg-white">
            <label class="input-title fw-bold m-0">
              {{
                range.start
                  ? `${departureText} - ${returnText}`
                  : content.planner_datePicker_depart_return
              }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
// Imports
import { computed, ComputedRef, onMounted, ref, watch } from "vue";
import { DatePicker as VDatePicker } from "v-calendar";
// Removed PopupBase import - using custom popup now
import {
  parseDateString,
  formatDateToString,
  formatDateRange,
  getDiffrenceDatesInNight,
} from "@/vue/planning/utils/datePickerHelpers";
import { AttributesRef, DateInfo, RangeType } from "@/types/datePicker";
import {
  getLanguageCode,
  getAdjacentMonth,
  parseHashParams,
  getSasonBasedOnDate,
  formatDateForHash,
} from "@/utils/utils";
import { HashParams } from "@/types";
import * as _ from "lodash";

import { onClickOutside } from "@vueuse/core";
// import store from '@/vue/planning/store/store'
import { useStore } from 'vuex'
const store = useStore();
import { format } from 'date-fns'
import { airportCodeMap } from "@/config/airportCodeMap";
import { isMobile as checkMobile } from "@/utils/utils";
import ClsBtn from '@/vue/common/clsBtn.vue'

// Props
interface Props {
  show?: boolean
}

const props = defineProps<Props>()

// Emit
interface Emit {
  (e: "close"): void
}

const emit = defineEmits<Emit>()

// Dynamic Refs - Dates Handler
const content = ref<any>(store.state.content);
const startDate = ref<Date | null>(parseDateString(store.state.dates["BeginDate"]));
const endDate = ref<Date | null>(parseDateString(store.state.dates["EndDate"]));
const availableDates = ref<Date[]>([]);
const selectedDate = ref<Date | null>(startDate.value);
const prevSelectedDate = ref<Date | null>(null);
const currentMonth = ref<string>("");
const currentYear = ref<string>("");
const range = ref<RangeType>({ start: "", end: "" });
const isMobile = ref<boolean>(false);

const attributesRef = ref<AttributesRef[]>([
  {
    key: "selected-dates",
    highlight: 'blue',
    dates: { start: new Date(), end: new Date() },
  },
]);


// Dynamic Refs - Elements Handler
const datePickerContainerRef = ref<HTMLElement | null>(null);
const datePickerRef = ref<InstanceType<typeof VDatePicker> | null>(null);
const isOpenedRef = ref<boolean>(props.show || false);
const selectedColor = ref("blue");

// Dynamic Refs - Miscellaneous
const isSelectingEndDateMode = ref<boolean>(false);
const generalParams = ref({
  locale: store.getters.locale,
  packCode: store.state.packCode || '',
  liheId: window.lihe.id,
});

const availabilitiesParams = ref({
  season: "",
});

const startDatesParams = ref<Record<string, string>>({
  month: "",
});

const locale = ref(getLanguageCode(store.getters.locale));

const flightOrigin = computed<string>(() => {
  let origin = ""

  if (store.state.flightOrigin && store.state.flightOrigin !== airportCodeMap.get("None")?.name) {
    origin = store.state.flightOrigin
  }

  return origin
})

//Computed
const departureText = computed((): string => {
  if (!startDate.value) return "";
  return formatDateRange(store.getters.locale, startDate.value);
});

const returnText = computed((): string => {
  if (!endDate.value) return "";
  return `${formatDateRange(store.getters.locale, endDate.value)}`;
});

// Methods
const handleSearch = async (departureDate: Date, returnDate: Date) => {
  // startDate.value = formatDateToString(departureDate);
  // endDate.value = formatDateToString(returnDate);
  startDate.value = departureDate;
  endDate.value = returnDate;

  const datesObj = {
    BeginDate: formatDateToString(departureDate),
    EndDate: formatDateToString(returnDate),
  };

  store.dispatch("setDates", datesObj);
  isSelectingEndDateMode.value = false;

  toggleShowHideDatePicker(false);

  // Update the range ref
  range.value.start = formatDateToString(departureDate)!;
  range.value.end = formatDateToString(returnDate)!;

  // Update the attributesRef
  attributesRef.value[0].dates = {
    start: departureDate,
    end: returnDate,
  };

  // Display again the available start dates for the months in display
  const currentMonth = departureDate ? departureDate.getMonth() + 1 : 1;
  await fetchStartDates(currentMonth.toString().padStart(2, "0"), true);
};

const selectDate = async (date: DateInfo): Promise<void> => {
  if (date.isDisabled) return;

  // Format the date to match the expected format for getSasonBasedOnDate
  const isDatePicker = true
  const formattedDate = formatDateForHash(date.id, isDatePicker);

  // Check if we need to switch season based on selected date
  const seasonBasedOnDate = getSasonBasedOnDate(formattedDate);

  if (seasonBasedOnDate !== store.state.season) {
    store.commit("setSeason", seasonBasedOnDate);
    window.seasonManager.switchSeasonWithoutReloading();

    // Update params with new season
    availabilitiesParams.value.season = seasonBasedOnDate;
  }

  if (isSelectingEndDateMode.value) {
    // User is selecting return date, complete the selection
    const returnDate = parseDateString(date.id);
    if (!prevSelectedDate.value || !returnDate) return;
    await handleSearch(prevSelectedDate.value!, returnDate);
    return;
  }

  // User is selecting departure date
  if (!selectedDate.value) return;

  // First click in a valid date will turn isSelectingEndDateMode into 'true'
  isSelectingEndDateMode.value = true;

  // Store the departure date
  prevSelectedDate.value = _.cloneDeep(selectedDate.value);

  // Update selectedDate to the newly selected departure date
  selectedDate.value = parseDateString(date.id);

  // Fetch endDates for the selected departure date
  startDatesParams.value.month = date.id.split("-").slice(0, 2).join("-");

  if (flightOrigin.value && store.state.transportationSelected) {
    startDatesParams.value.origin = flightOrigin.value
  }

  try {
    const res = await window.searchEngineService.getRelatedPackages(
      {
        ...generalParams.value,
        ...startDatesParams.value,
        startDate: date.id,
      },
      "getEndDates"
    ); 
    if (!isDateInView(res[0])) {
      jumpToDate(new Date(res[0]));
    }

    // Clean attributesRef and the availableDates section, and add the 'res' to availableDates it
    (attributesRef.value[0].dates = null), (availableDates.value = formatDatesArray(res));

  } catch (error) {
    console.error("Error fetching end dates at selectDate:", error);
  }
};


const toggleShowHideDatePicker = (show?: boolean) => {
  const pickerDiv = document.getElementById(
    "datePickerDiv"
  ) as HTMLElement | null;

  if (typeof show == "undefined"){
    show = isMobile.value ? false : !isOpenedRef.value;
  }

  if (show) {
    // Hide body overflow on mobile when popup opens (set immediately, not in setTimeout)
    if (isMobile.value) {
      document.body.style.overflow = 'hidden';
    }
    if (!pickerDiv || !datePickerContainerRef.value) return;
    setTimeout(() => {
      document.addEventListener("click", unfocuseDatePicker);
      pickerDiv.style.color = "var(--primary-blue)";
    }, 1);
  } else {
    // Restore body overflow when popup closes (do this first, before early return)
    if (isSelectingEndDateMode.value) {
      // Reset begin date selection - forget the selected departure date
      isSelectingEndDateMode.value = false;
      selectedDate.value = startDate.value; // Reset to original departure date from store
      prevSelectedDate.value = null; // Clear the temporary departure date
      
      // Reset availableDates to show start dates instead of end dates
      if (currentMonth.value && currentYear.value) {
        fetchStartDates(currentMonth.value);
      }
      
      // Reset attributesRef to show original date range
      if (startDate.value && endDate.value) {
        attributesRef.value[0].dates = {
          start: startDate.value,
          end: endDate.value,
        };
      } else {
        attributesRef.value[0].dates = null;
      }
    }
    if (isMobile.value) {
      document.body.style.overflow = 'auto';
    }
    if (!pickerDiv || !datePickerContainerRef.value) return;
    document.removeEventListener("click", unfocuseDatePicker);
    pickerDiv.style.color = "black";
    emit("close")
  }

  isOpenedRef.value = show;
};

const handleMobilePopupClick = (event: MouseEvent) => {
  // Close popup when clicking on the backdrop (outside the content)
  if (event.target === event.currentTarget) {
    toggleShowHideDatePicker(false);
  }
};


// Removed handleMobileClose function - using custom popup now

const unfocuseDatePicker = (event: MouseEvent) => {
  // Allow closing even when selecting end date - will reset the begin date selection
  const isDatePickerContainerRef = document.querySelector(".datePickerContainerRef")?.contains(event.target as Node);
  const isShowDatePickerBtn = document.querySelector(".showDatePickerBtn")?.contains(event.target as Node);
  if (event.target && !isDatePickerContainerRef && !isShowDatePickerBtn) {
    toggleShowHideDatePicker(false);
  }
};

const moveDatePickerMonth = async (month: string, year: string) => {
  await datePickerRef.value?.move({
    month: +month,
    year: +year,
  });
};

// Map and filter out null values, if they exist
const formatDatesArray = (array: string[]) => {
  const formattedArray = array
    .map((startDate: string) => parseDateString(startDate))
    .filter((date): date is Date => date !== null); // Type guard to ensure only valid Dates are kept;

  return formattedArray;
};


const isFetchingDatesTemporarily = ref(false);

const handlePagesChange = async (pages: { month: number; year: number }[]) => {
  const firstPage = pages[0]; // only handle left column for simplicity
  const formattedMonth = firstPage.month.toString().padStart(2, "0");
  const formattedYear = firstPage.year.toString();

  const isSameMonthYear =
    currentMonth.value === formattedMonth &&
    currentYear.value === formattedYear;

  if (!isSameMonthYear && !isFetchingDatesTemporarily.value && !isSelectingEndDateMode.value) {
    currentMonth.value = formattedMonth;
    currentYear.value = formattedYear;

    isFetchingDatesTemporarily.value = true;

    // Debounce by locking 500ms
    setTimeout(() => {
      isFetchingDatesTemporarily.value = false;
    }, 500);

    await fetchStartDates(currentMonth.value);
  }
};

const fetchStartDates = async (
  monthToFetch: string,
  isHandleSearch?: boolean
): Promise<void | null> => {
  if (!currentYear.value || !monthToFetch) return null;

  const monthPlusOne: string = getAdjacentMonth(monthToFetch, "next");
  const monthPlusTwo: string = getAdjacentMonth(monthPlusOne, "next");
  const monthMinusOne: string = getAdjacentMonth(monthToFetch, "prev");
  const monthMinusTwo: string = getAdjacentMonth(monthMinusOne, "prev");

  const monthsToFetch: string[] = [monthMinusTwo, monthMinusOne, monthToFetch, monthPlusOne, monthPlusTwo]

  try {
    // Fetch start dates for each month
    const startDatesPromises: Promise<string[]>[] = monthsToFetch.map(
      (month) => {
        startDatesParams.value.month = `${currentYear.value}-${month}`;

        if (flightOrigin.value && store.state.transportationSelected) {
          startDatesParams.value.origin = flightOrigin.value
        }

        return window.searchEngineService.getStartDates({
          ...generalParams.value,
          ...startDatesParams.value,
        });
      }
    );

    // Resolve all start dates
    const resolvedStartDates: string[][] = await Promise.all(
      startDatesPromises
    );

    // Format and collect all start dates
    const allStartDates = resolvedStartDates.flatMap((startDates) =>
      formatDatesArray(startDates)
    );

    // Remove duplicates and update availableDates
    availableDates.value = isHandleSearch ?
      [
        ...new Set([...allStartDates]),
      ] :
      [
        ...new Set([...availableDates.value, ...allStartDates]),
      ];
  } catch (error) {
    console.error("Error fetching start dates at fetchStartDates:", error);
  }
};

const generateDisabledDates = (available: Date[]): Date[] => {
  const result: Date[] = [];
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  startDate.setFullYear(today.getFullYear() - 2);
  endDate.setFullYear(today.getFullYear() + 2);

  const availableSet = new Set(available.map(date => format(date, 'yyyy-MM-dd')));

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dayStr = format(d, 'yyyy-MM-dd');
    if (!availableSet.has(dayStr)) {
      result.push(new Date(d));
    }
  }
  return result;
};

const disabledDates = computed(() => generateDisabledDates(availableDates.value));

/* handle initial data */
const preFill = async () => {
  if (!startDate.value || !endDate.value) return;
  if (!attributesRef.value[0].dates) return;

  // Set initial data
  attributesRef.value[0].dates.start = startDate.value;
  attributesRef.value[0].dates.end = endDate.value;

  range.value.start = store.state.dates["BeginDate"];
  range.value.end = store.state.dates["EndDate"];

  const beginDateFromStore: string = store.state.dates["BeginDate"];
  const monthFromBeginDate: string = beginDateFromStore.split("/")[1];
  const yearFromBeginDate: string = beginDateFromStore.split("/")[2];

  currentMonth.value = monthFromBeginDate;
  currentYear.value = yearFromBeginDate;

  // Fetch start dates
  await fetchStartDates(currentMonth.value);

  // Movo calendar to relevant date
  moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
};

 
const isDateInView = (date: string): boolean => {
    const dateElement = document.querySelector(`${isMobile?.value ? '.mobile-datepicker-popup' : ''} [class*="vc-day id-${date}"].in-month`)
    return dateElement != null
}
 
const jumpToDate = async (jumpToDate: Date) => {
    if (!datePickerRef.value) return;
 
    const currentYear = jumpToDate.getFullYear();
    const currentMonth = jumpToDate.getMonth();
 
    await datePickerRef.value.move({ month: currentMonth + 1, year: currentYear });
};

onMounted(() => {
  isMobile.value = checkMobile();
  // Only use onClickOutside for desktop, mobile has its own click handling
  if (!isMobile.value) {
    onClickOutside(datePickerContainerRef, () => toggleShowHideDatePicker(false))
  }
})

// Watches
// Watch for changes in store.state.packCode and reset the search
watch(
  () => store.state.packCode, // Reactive source
  async (newPackCode) => {
    // Parse the hash parameters
    const hashParams = parseHashParams() as HashParams;

    // Update params with new values for packCode and season
    generalParams.value.packCode = newPackCode;
    availabilitiesParams.value.season = hashParams.season;

    // Close the DatePicker, if it is open
    toggleShowHideDatePicker(false);

    // Prepare date
    const beginDateFromStore = store.state.dates["BeginDate"];
    const monthFromBeginDate = beginDateFromStore.split("/")[1];
    const yearFromBeginDate = beginDateFromStore.split("/")[2];

    // Fetch start dates
    await fetchStartDates(currentMonth.value);

    // Movo calendar to relevant date
    moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  }
);

watch(
  () => store.state.participants, // Reactive source
  (newBeginDate) => {
    // Close the DatePicker, if it is open
    toggleShowHideDatePicker(false);
  }
);

watch(() => props.show, (val) => toggleShowHideDatePicker(val))

// Watch for changes in store.state.dates["BeginDate"] and update startDate
watch(
  () => store.state.dates["BeginDate"], // Reactive source
  (newBeginDate) => {
    startDate.value = parseDateString(newBeginDate); // Update startDate
  }
);

// Watch for changes in store.state.dates["EndDate"] and update endDate
watch(
  () => store.state.dates["EndDate"], // Reactive source
  (newEndDate) => {
    endDate.value = parseDateString(newEndDate); // Update endDate
  }
);


watch(
  () => store.state.loading, // Reactive source
  (newEndDate) => {
    preFill();
  }
);

// watch(availableDates, (val) => {
//   console.log("Available Dates Ã¢â€ â€™", val.map(d => d.toDateString()));
//   console.log("Valid Dates?", val.every(d => d instanceof Date));
// });

// watch(() => props.show, (val) => {
//   toggleShowHideDatePicker(val)
// })

</script>

<style scoped>
.datepicker-input {
  z-index: 99;
}

.datePickerContainerRef {
  top: calc(100% + 4px);
  /* 4px gap below the input */
  left: 0;
  width: 100%;
  /* match the trigger's width */
  z-index: 1000000;
  /* above everything else */
}

.date-picker-root {
  position: relative;
}

/* optional: if .datepicker-input had its own overflow, overrule it */
.datepicker-input {
  overflow: visible !important;
}

.datepicker-input-label {
  font-size: 16px;
}

/* General calendar days */
:deep(.vc-day-content) {
  font-weight: 500 !important;
  width: 38px !important;
  height: 38px !important;
  font-size: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

:deep(.vc-highlight-content-solid) {
  background: var(--primary-blue);
  color: white !important;
}

:deep(.vc-highlight-content-solid:hover) {
  background-color: var(--primary-blue) !important;
}


/* Disabled day override */
:deep(.vc-day.disabled:not(.vc-highlight-content-solid):not(.vc-highlight-content-outline)),
:deep(.vc-day-content.disabled:not(.vc-highlight-content-solid):not(.vc-highlight-content-outline)) {
  pointer-events: none;
  color: var(--grey-400) !important;
  cursor: pointer !important;
  background-color: transparent !important;
}

:deep(.vc-highlight-content-outline) {
  border: none !important;
  outline: none !important;

  background: var(--primary-blue) !important;
  color: white !important;
}

:deep(.vc-highlight) {
  background-color: var(--blue-100) !important;
  height: 38px !important;
}

:deep(.vc-day:has(div.vc-highlights):not(:has(.vc-highlight-content-solid, .vc-highlight-content-outline)) .vc-day-content) {
  color: var(--primary-blue);
}

:deep(.vc-header) {
  background-color: transparent !important;
}

:deep(.vc-title) {
  background-color: transparent !important;
}

:deep(.vc-arrow) {
  background-color: transparent !important;
}

:deep(.vc-container [role=button], .vc-container button) {
  cursor: pointer;
}

:deep(.vc-pane-layout) {
  padding: 10px 30px;
  gap: 30px;
}

:deep(.vc-title span) {
  font-size: 14px !important;
}

:deep(.vc-title-wrapper button) {
  color: var(--grey-grey-900, #0D141B);
  text-align: center;
  font-size: inherit !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  background-color: transparent;
  font-weight: bold;
}

:deep(.vc-header is-lg) {
  margin-top: 0px !important;
}

:deep(.vc-weekday) {
  font-weight: 400 !important;
  font-size: inherit !important;
}

:deep(.vc-weeks) {
  margin-top: 15px;
}

/* days buttons */
:deep(.vc-day) {
  margin-bottom: 5px;
}

:deep(.vc-day:not(.disabled)) {
  font-size: 18px;
  font-weight: 800;
}

/* Mobile Custom Popup Styles */
.mobile-datepicker-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100001 !important;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Sticky Header and Footer for Mobile */
.datepicker-header-sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  width: 100%;
  border-bottom: 1px solid #e9ecef;
}

.datepicker-footer-sticky {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: white;
  width: 100%;
  border-top: 1px solid #e9ecef;
}

.datepicker-calendar-content {
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: 0 1rem;
}

.mobile-close-btn {
  transition: background-color 0.2s;
}

.mobile-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.mobile-close-btn svg {
  fill: #666;
}

/* Mobile-specific calendar adjustments */
@media (max-width: 768px) {
  .mobile-datepicker-content {
    padding: 0;
  }

  .datepicker-calendar-content {
    padding: 1rem;
  }

  :deep(.vc-pane-layout) {
    padding: 5px 15px;
    gap: 15px;
    border: none;
  }

  :deep(.vc-day-content) {
    width: 35px !important;
    height: 35px !important;
    font-size: 16px;
  }

  :deep(.vc-highlight) {
    height: 35px !important;
  }

  :deep(.vc-container) {
    border: none;
    width: 100%
  }
}
</style>